import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, rem } from '@mantine/core'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'

import { PATHS } from 'api/paths'
import { getChatbotApiPath } from 'api/utils/paths'
import { showNotification } from 'components/molecules/notificationsHandler'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { CHAT_COUNTRY_CODE_BY_LOCALE } from 'interfaces/constants'

import useStyles from './index.styles'
import { AIChatBotContext } from '../../context'
import { ChatBody } from '../chatBody'
import { HightlightMessage } from '../hightlightMessage'
import { Message } from '../message'
import { MessageInput } from '../messageInput'
import { StreamingMessage } from '../messageStream'
import { readMessageStream } from '../messageStream/utils'
import { TimeBar } from '../timer'
import { TypingIndicator } from '../typingIndicator'

type Props = { starterMessage?: MessageProps } & BoxProps

export type MessageStreamType = ReadableStream<Uint8Array>

export type MessageProps = {
  type: 'incoming' | 'incomingWithNoAvatar' | 'current' | 'highlightMessage'
  message?: ReactNode | string
  date?: Date
}

export const MessageList = ({
  starterMessage,
  children,
  styles,
  classNames,
  className,
  ...boxProps
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale, query } = useRouterExtend()
  const { utm_campaign = '', utm_medium = '', utm_source = '' } = query
  const { classes } = useStyles(undefined, {
    name: 'AIChatBotWidget__MessageList',
    styles,
    classNames,
  })
  const currentUrl = useCurrentUrl()
  const {
    showHighlightMessage,
    chatId,
    messageList,
    numberToShowHighlightMess,
    onClickHighlightMess,
    expandWidget,
    setExpandWidget,
  } = useContext(AIChatBotContext)
  const isMobile = useIsMobile()
  const [loading, setLoading] = useState(false)
  const [stream, setStream] = useState<MessageStreamType>()
  const [disableTyping, setDisableTyping] = useState(false)
  const [messages, setMessages] = useState<MessageProps[]>(messageList || [])
  const [showErr, setShowErr] = useState(false)
  const [showHightlight, setShowHighlight] = useState(false)
  const suggestQuestions = useRef([
    {
      id: 1,
      content: 'Anak saya diare setelah minum susu, kenapa ya?',
      priority: 4,
    },
    {
      id: 2,
      content:
        'Apakah anak batuk setelah minum susu termasuk reaksi alergi susu?',
      priority: 5,
    },
    {
      id: 3,
      content:
        'Bagaimana cara mengatasi gejala tidak cocok susu sapi pada si Kecil?',
      priority: 1,
    },
    {
      id: 4,
      content:
        'Apakah ada alternatif susu yang aman untuk si Kecil yang tidak cocok susu sapi?',
      priority: 3,
    },
    {
      id: 5,
      content:
        'Bagaimana cara memastikan si Kecil mendapatkan nutrisi yang cukup jika tidak bisa mengonsumsi susu sapi?',
      priority: 2,
    },
  ])

  useEffect(() => {
    if (messageList) {
      setMessages(messageList || [])
      if (
        messageList.length === 1 &&
        messageList?.[0]?.message &&
        typeof messageList[0].message === 'string'
      ) {
        handleSubmitMessageText(messageList[0].message)
      }
    }
  }, [messageList])

  useEffect(() => {
    showHighlightMessage && !showHightlight && handleFormatListMess()
  }, [messages])

  const handleSubmitMessageText = useCallback(async (message: string) => {
    setShowErr(false)
    if (!message) return
    handleSendMessage(message)
  }, [])

  const handleSendMessage = useCallback(async (message: string) => {
    setLoading(true)
    setDisableTyping(true)
    !expandWidget && setExpandWidget(true)
    fetch(
      getChatbotApiPath(PATHS.CHATBOT.SEND_MESSAGE, {
        project: 'sgm-soya-chat',
      }),
      {
        method: 'POST',
        body: JSON.stringify({
          user_id: Cookies.get('_ga'),
          chat_id: chatId,
          message: message,
          country_code: CHAT_COUNTRY_CODE_BY_LOCALE[locale],
          referrer: `${window.location.origin}${window.location.pathname}`,
          utm_campaign,
          utm_medium,
          utm_source,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
      .then((response) => response.body)
      .then((rb) => {
        const reader = rb?.getReader()
        return new ReadableStream({
          start(controller) {
            // The following function handles each data chunk
            function push() {
              // "done" is a Boolean and value a "Uint8Array"
              reader?.read().then(({ done, value }) => {
                // If there is no more data to read
                if (done) {
                  controller.close()
                  return
                }
                // Get the data and send it to the browser via the controller
                controller.enqueue(value)
                // Check chunks by logging to the console
                // console.log(done, value)
                push()
              })
            }
            push()
          },
        })
      })
      .then((stream) => {
        const [stream1, stream2] = stream.tee()
        setLoading(false)
        setStream(stream1)
        return readMessageStream(stream2)
      })
      .then((result) => {
        setStream(undefined)
        setMessages((v) => [
          ...v,
          {
            type: 'incoming',
            message: result,
          },
          ...(suggestQuestions.current.length > 0
            ? [
                {
                  type: 'incomingWithNoAvatar',
                  message: (
                    <>
                      <Text size="p4">
                        Topik lain seputar tidak cocok susu sapi
                      </Text>
                      {suggestQuestions.current
                        .sort((a, b) => a.priority - b.priority)
                        .slice(0, 2)
                        .map((i, idx) => (
                          <button
                            key={idx}
                            className="content pointer-event-child-none"
                            data-event-category="Chatbot"
                            data-event-action={`Click - ${i.content}`}
                            data-event-label={currentUrl}
                            onClick={() => {
                              handleSubmitMessageText(i.content)
                              setMessages((v) => [
                                ...v,
                                {
                                  type: 'current',
                                  message: i.content,
                                },
                              ])
                              suggestQuestions.current = suggestQuestions.current.filter(
                                (j) => j.id !== i.id,
                              )
                            }}
                          >
                            <Text size={isMobile ? 's3' : 's4'}>
                              {i.content}
                            </Text>
                          </button>
                        ))}
                    </>
                  ),
                } as MessageProps,
              ]
            : []),
        ])
        setDisableTyping(false)
        return result
      })
      .catch((error) => {
        setStream(undefined)
        setDisableTyping(false)
        setLoading(false)
        setShowErr(true)
        console.log(error)
        showNotification({
          title: '',
          message: f({ id: 'notification.comment.somethingWrong' }),
          type: 'danger',
        })
      })
  }, [])

  const handleFormatListMess = useCallback(() => {
    setMessages((oldList: MessageProps[]) => {
      if (
        numberToShowHighlightMess > 0 &&
        oldList.filter((i) => i.type === 'incoming').length >=
          numberToShowHighlightMess
      ) {
        setShowHighlight(true)
      }
      return oldList
    })
  }, [])

  return (
    <ChatBody
      scrollToBottom
      classNames={{
        action: classes.action,
        scrollBody: classes.scrollBody,
      }}
      actionState={
        showHightlight || loading || showErr ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: rem(10),
            }}
          >
            {showHightlight && (
              <HightlightMessage
                className={classes.highlightMessage}
                heading="Ambil Voucher Diskon Rp 12,000 Di Sini!"
                description="Berikan Nutrisi Tepat untuk si Kecil Tidak Cocok Susu Sapi di Sini"
                data-event-category="Chatbot"
                data-event-action="Form Open Click"
                data-event-label={currentUrl}
                onClick={onClickHighlightMess}
              />
            )}
            {loading && <TypingIndicator className={classes.indicator} />}
            {showErr && (
              <Text size="n1" color="red" className={classes.errorTxt}>
                {f({
                  id: 'chatbot.messageErr',
                  defaultMessage:
                    'There was an error sending the message, please try again.',
                })}
              </Text>
            )}
          </Box>
        ) : undefined
      }
      action={
        <MessageInput
          disabled={disableTyping}
          onSubmit={({ message }) => {
            setMessages((v) => [
              ...v,
              {
                message,
                type: 'current',
              },
            ])
            handleSubmitMessageText(message)
          }}
        />
      }
      {...boxProps}
    >
      <Box className={classes.root}>
        <TimeBar date={messages?.[0]?.date} className={classes.timeBar} />
        {messages.map((i, idx) => (
          <Message key={String(idx)} message={i.message} type={i.type} />
        ))}
        {stream && <StreamingMessage stream={stream} />}
      </Box>
    </ChatBody>
  )
}
